import { useCallback, useEffect, useState } from 'react';

/**
 * "Open search on card tap": a card the site knows opens the desk palette with
 * that card preselected; off, the tap raises a toast that offers the same. It
 * replaced "auto-navigate" with the same default (on) and keeps its storage
 * key, so a desk that had switched auto-navigate off keeps taps quiet.
 */
export const CARD_TAP_OPEN_SEARCH_STORAGE_KEY = 'card-tap-autonavigate';
const CARD_TAP_OPEN_SEARCH_EVENT = 'card-tap-open-search-change';

function readOpenSearch(): boolean {
  if (typeof window === 'undefined') return true;
  try {
    return window.localStorage.getItem(CARD_TAP_OPEN_SEARCH_STORAGE_KEY) !== '0';
  } catch {
    return true;
  }
}

function writeOpenSearch(enabled: boolean): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(CARD_TAP_OPEN_SEARCH_STORAGE_KEY, enabled ? '1' : '0');
  } catch {
    // Ignore storage failures; in-memory state still updates for this session.
  }
  window.dispatchEvent(new CustomEvent(CARD_TAP_OPEN_SEARCH_EVENT, { detail: { enabled } }));
}

export function useCardTapOpenSearch() {
  const [enabled, setEnabledState] = useState(readOpenSearch);

  useEffect(() => {
    const sync = () => setEnabledState(readOpenSearch());
    window.addEventListener('storage', sync);
    window.addEventListener(CARD_TAP_OPEN_SEARCH_EVENT, sync);
    return () => {
      window.removeEventListener('storage', sync);
      window.removeEventListener(CARD_TAP_OPEN_SEARCH_EVENT, sync);
    };
  }, []);

  const setEnabled = useCallback((next: boolean) => {
    setEnabledState(next);
    writeOpenSearch(next);
  }, []);

  return { enabled, setEnabled };
}
